/**
 * Trim range parsing and validation.
 *
 * This is the part of Trim that is genuinely real. Reading "1:20" out of a text field,
 * deciding whether 1:20 → 0:40 is a range anybody meant, and working out that the
 * result would be 2:45 of a 4:05 track are all things the frontend can do correctly
 * with no service behind it — so they are done correctly here, unit tested, and kept
 * out of the component.
 *
 * What the frontend CANNOT do is produce the shortened audio. There is no audio at
 * all (see api/generationApi.ts) and no model that edits it (see api/audioEditApi.ts,
 * which names the gap). So this module deliberately stops at the range: it never
 * returns a Song, a version or a length for a track that was not actually cut.
 *
 * Strict on purpose, unlike parseLength in lib/formatting.ts. That function exists to
 * render a stored "3:12" and tolerates rubbish by reading it as 0 — which is right for
 * display and wrong for a field a user types into, where silently reading "1:9" as
 * 1:00 would trim to a place they did not ask for.
 */

/** A validated selection, in seconds from the start of the track. */
export interface TrimRange {
  start: number;
  end: number;
}

/** What `resolveTrimRange` concluded: a range, or the reason there isn't one. */
export interface TrimRangeResult {
  /** The selection, or null when the input does not describe one. */
  range: TrimRange | null;
  /**
   * The single most useful complaint, in the user's words, or null when the range is
   * good. One message rather than a list: the fields are checked in the order they
   * are read, so the first problem is the one to fix first.
   */
  error: string | null;
  /** Which field the error belongs to, so the panel can mark it. */
  field: 'start' | 'end' | null;
}

/** What `resolveSplitPoint` concluded: one split position, or the reason there is not one. */
export interface SplitPointResult {
  /** Seconds from the start of the track, or null while the field is invalid. */
  splitAt: number | null;
  /** The single complaint worth showing, or null when the split point is usable. */
  error: string | null;
}

/**
 * Reads a timecode a user typed.
 *
 * Accepts `m:ss`, `mm:ss` and a bare number of seconds — the three forms someone
 * looking at a "2:48" duration readout actually types. Returns null for anything
 * else, including the near-misses that matter:
 *
 *   "1:9"   — ambiguous. Nine seconds or ninety? Rejected rather than guessed.
 *   "0:60"  — not a time. Sixty seconds is 1:00, and accepting it would make two
 *             spellings of one position.
 *   "-5"    — no leading sign is accepted, which is why nothing downstream has to
 *             check for a negative start.
 *   ""      — an empty field is not a zero.
 */
export function parseTimecode(input: string): number | null {
  const text = input.trim();
  if (!text) return null;

  const clock = /^(\d+):([0-5]\d)$/.exec(text);
  if (clock) return Number(clock[1]) * 60 + Number(clock[2]);

  // A bare count of seconds. Bounded to a sane number of digits so a pasted phone
  // number does not become a nine-hour timecode.
  if (/^\d{1,5}$/.test(text)) return Number(text);

  return null;
}

/** Formats seconds as the `m:ss` the fields and readouts use. */
export function formatTimecode(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${(whole % 60).toString().padStart(2, '0')}`;
}

/**
 * Validates the two fields against the track's length.
 *
 * `duration` is the song's own length in seconds. A track with no known length cannot
 * be trimmed against anything, so that is reported rather than treated as unbounded —
 * an end time no larger than nothing would reject every input with a confusing
 * message about the end of the song.
 */
export function resolveTrimRange(
  startText: string,
  endText: string,
  duration: number
): TrimRangeResult {
  if (!(duration > 0)) {
    return { range: null, error: 'This track has no known length to trim against.', field: null };
  }

  const start = parseTimecode(startText);
  if (start === null) {
    return { range: null, error: 'Enter a start time like 0:30.', field: 'start' };
  }

  const end = parseTimecode(endText);
  if (end === null) {
    return { range: null, error: 'Enter an end time like 1:45.', field: 'end' };
  }

  // The end is checked against the track before the pair is checked against itself,
  // because "past the end of the song" tells you more than "start must come first"
  // when both are true — which they are whenever someone types a time from a
  // different, longer track.
  if (end > duration) {
    return {
      range: null,
      error: `The song ends at ${formatTimecode(duration)}.`,
      field: 'end',
    };
  }
  if (start >= duration) {
    return {
      range: null,
      error: `The song ends at ${formatTimecode(duration)}.`,
      field: 'start',
    };
  }
  /*
    Equal ends are caught here rather than by a separate minimum-length check. Both
    fields parse to whole seconds — parseTimecode accepts no fractional form — so the
    shortest expressible range is one second, and `start >= end` is already the whole
    of "too short". A `MIN_TRIM_SECONDS` guard below this line would be a branch no
    input can reach, carrying a message nobody can be shown. If sub-second precision
    ever arrives, it arrives in parseTimecode and the guard belongs here then.
  */
  if (start >= end) {
    return { range: null, error: 'The start time must come before the end time.', field: 'end' };
  }

  return { range: { start, end }, error: null, field: null };
}

/** Validates Split's one field against the track's length. */
export function resolveSplitPoint(splitText: string, duration: number): SplitPointResult {
  if (!(duration > 0)) {
    return { splitAt: null, error: 'This track has no known length to split against.' };
  }

  const splitAt = parseTimecode(splitText);
  if (splitAt === null) {
    return { splitAt: null, error: 'Enter a split point like 1:45.' };
  }
  if (splitAt > duration) {
    return { splitAt: null, error: `The song ends at ${formatTimecode(duration)}.` };
  }
  if (splitAt <= 0 || splitAt >= duration) {
    return { splitAt: null, error: 'Choose a split point inside the song.' };
  }

  return { splitAt, error: null };
}

/** How long the trimmed track would be, in seconds. */
export function trimmedLength(range: TrimRange): number {
  return Math.max(0, range.end - range.start);
}

/**
 * The shortest range the fields can express.
 *
 * Both fields parse to whole seconds (parseTimecode accepts no fractional form), so one
 * second is the floor — and it is the gap the two drag handles must always keep between
 * them, the same condition `resolveTrimRange` enforces as `start >= end`.
 */
export const MIN_TRIM_SECONDS = 1;

/**
 * Where a pointer at fraction `f` of the lane's width lands, in whole seconds.
 *
 * The lane spans 0…duration left to right, so a press at 25% of a 240s track is 60s. The
 * inverse of `trimSelectionPercent`: that turns seconds into a position for drawing, this
 * turns a position back into seconds for dragging. Rounded to whole seconds because that
 * is the finest the fields express, so a drag can only ever set a value the user could
 * also have typed — the overlay and the fields stay two views of one number.
 */
export function trimSecondsAtFraction(fraction: number, duration: number): number {
  if (!(duration > 0)) return 0;
  const clamped = Math.max(0, Math.min(1, fraction));
  return Math.round(clamped * duration);
}

/**
 * Places one edge of the range at `seconds`, keeping the pair a valid selection.
 *
 * Dragging an edge must never let it meet or cross the other one, so the moved edge is
 * bounded by its neighbour (minus/plus MIN_TRIM_SECONDS) and by the track. Returns the
 * whole range so the caller sets both fields from a single source and they cannot drift.
 */
export function moveTrimEdge(
  range: TrimRange,
  edge: 'start' | 'end',
  seconds: number,
  duration: number
): TrimRange {
  if (edge === 'start') {
    const start = Math.max(0, Math.min(seconds, range.end - MIN_TRIM_SECONDS));
    return { start, end: range.end };
  }
  const end = Math.min(duration, Math.max(seconds, range.start + MIN_TRIM_SECONDS));
  return { start: range.start, end };
}

/**
 * Shifts the whole selection by `deltaSeconds`, preserving its length.
 *
 * Dragging the body moves both edges together; at either end of the track it stops rather
 * than compressing, so the kept length the readout promises does not silently change as
 * the selection is slid around. `deltaSeconds` may be negative.
 */
export function shiftTrimRange(range: TrimRange, deltaSeconds: number, duration: number): TrimRange {
  const width = range.end - range.start;
  const maxStart = Math.max(0, duration - width);
  const start = Math.max(0, Math.min(range.start + deltaSeconds, maxStart));
  return { start, end: start + width };
}

/**
 * The selection as a fraction of the track, for drawing it over the waveform.
 *
 * Percentages rather than pixels, so the overlay is correct at every width without
 * measuring anything — which matters because the lane is a flex child that changes
 * width at three breakpoints.
 */
export function trimSelectionPercent(
  range: TrimRange,
  duration: number
): { left: number; width: number } {
  if (!(duration > 0)) return { left: 0, width: 0 };
  const clamp = (n: number) => Math.max(0, Math.min(100, n));
  const left = clamp((range.start / duration) * 100);
  const right = clamp((range.end / duration) * 100);
  return { left, width: Math.max(0, right - left) };
}
